import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name?: string;
  authority: string;
}

export interface TwoFaProvider {
  type: string;
  default: boolean;
  contact?: string;
  minVerificationCodeSendPeriod?: number;
}

export type LoginResult =
  | { success: true }
  | { success: false; error: string }
  | { requires2FA: true; preVerificationToken: string; providers: TwoFaProvider[] };

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verify2FA: (preVerificationToken: string, providerType: string, verificationCode: string) => Promise<boolean>;
  send2FACode: (preVerificationToken: string, providerType: string) => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to decode JWT token and get expiration time
  const getTokenExpiration = (token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Helper function to check if token needs refresh (5 minutes before expiry)
  const shouldRefreshToken = (token: string): boolean => {
    const expTime = getTokenExpiration(token);
    if (!expTime) return false;
    
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return fiveMinutesFromNow >= expTime;
  };

  // Start proactive token refresh service
  const startTokenRefreshService = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(async () => {
      const token = localStorage.getItem('tb_token');
      const refreshTokenValue = localStorage.getItem('tb_refresh_token');
      
      if (token && refreshTokenValue && user && shouldRefreshToken(token)) {
        console.log('Proactively refreshing token before expiration');
        const success = await refreshToken();
        if (!success) {
          console.warn('Proactive token refresh failed, user may need to login again');
        }
      }
    }, 60000); // Check every minute
  };

  // Stop proactive token refresh service
  const stopTokenRefreshService = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem('tb_refresh_token');
      if (!refreshTokenValue) {
        return false;
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      if (response.ok) {
        const { token, refreshToken: newRefreshToken } = await response.json();
        localStorage.setItem('tb_token', token);
        localStorage.setItem('tb_refresh_token', newRefreshToken);
        return true;
      } else {
        // Refresh failed, clear tokens
        localStorage.removeItem('tb_token');
        localStorage.removeItem('tb_refresh_token');
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      localStorage.removeItem('tb_token');
      localStorage.removeItem('tb_refresh_token');
      setUser(null);
      return false;
    }
  };

  const checkAuthStatus = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('tb_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Verify token with backend
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        startTokenRefreshService();
      } else {
        // Token is invalid, try to refresh if we have a refresh token
        const refreshTokenValue = localStorage.getItem('tb_refresh_token');
        if (refreshTokenValue) {
          const refreshSuccess = await refreshToken();
          if (refreshSuccess) {
            // Re-verify with new token
            const newToken = localStorage.getItem('tb_token');
            if (newToken) {
              const verifyResponse = await fetch('/api/auth/verify', {
                headers: { 'Authorization': `Bearer ${newToken}` }
              });
              if (verifyResponse.ok) {
                const userData = await verifyResponse.json();
                setUser(userData);
                startTokenRefreshService();
              }
            }
          } else {
            // Refresh failed, clear tokens and logout
            localStorage.removeItem('tb_token');
            localStorage.removeItem('tb_refresh_token');
            setUser(null);
            stopTokenRefreshService();
          }
        } else {
          // No refresh token, clear access token
          localStorage.removeItem('tb_token');
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('tb_token');
      localStorage.removeItem('tb_refresh_token');
      setUser(null);
      stopTokenRefreshService();
    } finally {
      setIsLoading(false);
    }
  };

  // Check for existing session on app start
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Auth verification on mount must call setState after async token check
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkAuthStatus is intentionally excluded to run only on mount
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      stopTokenRefreshService();
    };
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();

        // Check if 2FA is required
        if (data.requires2FA) {
          return {
            requires2FA: true,
            preVerificationToken: data.preVerificationToken,
            providers: data.providers
          };
        }

        // Normal login — store tokens and set user
        const { token, refreshToken: refreshTokenValue, user: userData } = data;
        localStorage.setItem('tb_token', token);
        localStorage.setItem('tb_refresh_token', refreshTokenValue);
        setUser(userData);
        startTokenRefreshService();
        return { success: true };
      } else {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch {
          // Response doesn't contain valid JSON, use generic error message
          errorMessage = `Authentication failed (${response.status})`;
        }
        console.error('Login failed:', errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  const verify2FA = async (
    preVerificationToken: string,
    providerType: string,
    verificationCode: string
  ): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preVerificationToken, providerType, verificationCode }),
      });

      if (response.ok) {
        const { token, refreshToken: refreshTokenValue, user: userData } = await response.json();
        localStorage.setItem('tb_token', token);
        localStorage.setItem('tb_refresh_token', refreshTokenValue);
        setUser(userData);
        startTokenRefreshService();
        return true;
      }
      return false;
    } catch (error) {
      console.error('2FA verification error:', error);
      return false;
    }
  };

  const send2FACode = async (
    preVerificationToken: string,
    providerType: string
  ): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/2fa/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preVerificationToken, providerType }),
      });
      return response.ok;
    } catch (error) {
      console.error('2FA send code error:', error);
      return false;
    }
  };

  const logout = () => {
    stopTokenRefreshService();
    localStorage.removeItem('tb_token');
    localStorage.removeItem('tb_refresh_token');
    setUser(null);

    // Redirect to home — _app.tsx renders LoginForm when !isAuthenticated
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    verify2FA,
    send2FACode,
    logout,
    refreshToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}